<template>
  <div class="user-management-container appointments-style">
    <div class="user-table-card">
      <div class="user-table-header">
        <div class="user-table-title">User Management</div>
        <ion-button class="add-user-button-header" color="dark" @click="showAddUserModal = true">
          <ion-icon :icon="personAddOutline" slot="start"></ion-icon>
          Add User
        </ion-button>
      </div>
      <div class="user-table-columns">
        <div class="user-col username">Username</div>
        <div class="user-col email">Email</div>
        <div class="user-col roles">Roles</div>
        <div class="user-col actions">Actions</div>
      </div>
      <UserList
        :users="users"
        :loading="loading"
        @edit-user="openEditUser"
        @delete-user="confirmDeleteUser"
        @reset-password="showPasswordResetHelp"
      />
    </div>
    <!-- Password Reset Help Modal -->
    <ion-modal class="light-mode-modal" :is-open="showPasswordHelpModal" @didDismiss="handlePasswordModalDismiss" 
      style="--background: #ffffff !important; --ion-background-color: #ffffff !important; background: #ffffff !important; --height: auto; --min-height: 400px; --max-height: 80%;">
      <ion-header>
        <ion-toolbar>
          <ion-title>Password Reset</ion-title>
          <ion-buttons slot="end">
            <ion-button @click="handlePasswordModalDismiss">Close</ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding" style="--background: #ffffff !important; background: #ffffff !important; padding-bottom: 32px;">
        <div class="password-help-content">
          <h2>Password Reset</h2>
          
          <div class="user-driven-reset">
            <p>You're about to send a password reset link to <strong>{{ selectedUser?.username || 'this user' }}</strong>'s email address.</p>
            
            <ion-button expand="block" color="dark" @click="sendPasswordResetEmail(selectedUser)" :disabled="resettingPassword">
              {{ resettingPassword ? 'Sending...' : 'Send Password Reset Link' }}
            </ion-button>
            
            <p class="note">This will send an email with a secure link that allows the user to reset their own password.</p>
          </div>
        </div>
      </ion-content>
    </ion-modal>
    
    <!-- Add User Modal -->
    <ion-modal class="light-mode-modal" :is-open="showAddUserModal" @didDismiss="handleAddUserModalDismiss" @didPresent="onAddUserModalPresent" 
      style="--background: #ffffff !important; --ion-background-color: #ffffff !important; background: #ffffff !important; --height: auto; --min-height: 500px; --max-height: 80%;">
      <ion-header>
        <ion-toolbar>
          <ion-title>Add New User</ion-title>
          <ion-buttons slot="end">
            <ion-button @click="handleAddUserModalDismiss">Close</ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding" style="--background: #ffffff !important; background: #ffffff !important; padding-bottom: 32px;">
        <form @submit.prevent="createUser" class="compact-form">
          <ion-item class="compact-item">
            <ion-label position="stacked">Email <ion-text color="danger">*</ion-text></ion-label>
            <ion-input type="email" v-model="newUser.email" required></ion-input>
          </ion-item>
          
          <ion-item class="compact-item">
            <ion-label position="stacked">Username <ion-text color="danger">*</ion-text></ion-label>
            <ion-input type="text" v-model="newUser.username" required></ion-input>
          </ion-item>
          
          <ion-item class="compact-item">
            <ion-label position="stacked">Password <ion-text color="danger">*</ion-text></ion-label>
            <ion-input type="password" v-model="newUser.password" required :minlength="6"></ion-input>
            <ion-note>Password must be at least 6 characters</ion-note>
          </ion-item>
          
          <ion-list class="compact-list">
            <ion-list-header class="compact-header">
              <ion-label>User Roles</ion-label>
            </ion-list-header>
            
            <div class="role-toggles-container">
              <ion-item class="role-toggle-item compact-toggle">
                <ion-label>Admin</ion-label>
                <ion-toggle v-model="newUser.is_admin"></ion-toggle>
              </ion-item>
              
              <ion-item class="role-toggle-item compact-toggle">
                <ion-label>Super Admin</ion-label>
                <ion-toggle v-model="newUser.is_super_admin"></ion-toggle>
              </ion-item>
            </div>
          </ion-list>
          
          <div class="ion-margin-top" style="margin-bottom: 32px;">
            <ion-button type="submit" expand="block" color="dark" class="create-user-button" :disabled="creatingUser">
              {{ creatingUser ? 'CREATING...' : 'CREATE USER' }}
            </ion-button>
          </div>
        </form>
      </ion-content>
    </ion-modal>
    
    <!-- Edit User Modal -->
    <ion-modal class="light-mode-modal edit-user-force-black" :is-open="showEditUserModal" @didDismiss="handleEditUserModalDismiss" @didPresent="onEditUserModalPresent" 
      style="--background: #ffffff !important; --ion-background-color: #ffffff !important; background: #ffffff !important; --height: auto; --min-height: 500px; --max-height: 80%;">
      <ion-header>
        <ion-toolbar>
          <ion-title>Edit User</ion-title>
          <ion-buttons slot="end">
            <ion-button @click="handleEditUserModalDismiss">Close</ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding" style="--background: #ffffff !important; background: #ffffff !important; padding-bottom: 32px;">
        <form @submit.prevent="updateUser" v-if="editingUser" class="compact-form">
          <ion-item class="compact-item">
            <ion-label position="stacked">Email</ion-label>
            <ion-input type="email" v-model="editingUser.email" readonly></ion-input>
            <ion-note>Email cannot be changed</ion-note>
          </ion-item>
          
          <ion-item class="compact-item">
            <ion-label position="stacked">Username <ion-text color="danger">*</ion-text></ion-label>
            <ion-input type="text" v-model="editingUser.username" required></ion-input>
          </ion-item>
          
          <ion-list class="compact-list">
            <ion-list-header class="compact-header">
              <ion-label>User Roles</ion-label>
            </ion-list-header>
            
            <div class="role-toggles-container">
              <ion-item class="role-toggle-item compact-toggle">
                <ion-label>Admin</ion-label>
                <ion-toggle v-model="editingUser.is_admin"></ion-toggle>
              </ion-item>
              
              <ion-item class="role-toggle-item compact-toggle">
                <ion-label>Super Admin</ion-label>
                <ion-toggle v-model="editingUser.is_super_admin"></ion-toggle>
              </ion-item>
            </div>
          </ion-list>
          
          <div class="ion-margin-top" style="margin-bottom: 32px;">
            <ion-button type="submit" expand="block" color="dark" class="update-user-button" :disabled="updatingUser">
              {{ updatingUser ? 'UPDATING...' : 'UPDATE USER' }}
            </ion-button>
          </div>
        </form>
      </ion-content>
    </ion-modal>
    
    <!-- Confirm Delete Modal -->
    <ion-modal class="light-mode-modal" :is-open="showDeleteConfirmModal" @didDismiss="handleDeleteModalDismiss" 
      style="--background: #ffffff !important; --ion-background-color: #ffffff !important; background: #ffffff !important; --height: auto; --min-height: 400px; --max-height: 80%;">
      <ion-header>
        <ion-toolbar>
          <ion-title>Confirm Delete</ion-title>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding" style="--background: #ffffff !important; background: #ffffff !important; padding-bottom: 32px;">
        <div class="confirm-delete-content">
          <h2>Delete User?</h2>
          <p>Are you sure you want to delete the user <strong>{{ userToDelete?.username || 'this user' }}</strong>?</p>
          <p>This action cannot be undone.</p>
          
          <div class="confirm-actions" style="margin-bottom: 24px;">
            <ion-button color="medium" class="cancel-button" @click="handleDeleteModalDismiss">CANCEL</ion-button>
            <ion-button color="danger" class="delete-button" @click="deleteUser" :disabled="deletingUser">
              {{ deletingUser ? 'DELETING...' : 'DELETE' }}
            </ion-button>
          </div>
        </div>
      </ion-content>
    </ion-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, onBeforeUnmount } from 'vue';
import { supabase } from '@/supabase';
import { useUserStore } from '@/store/user';
import { useToast } from '@/composables/useToast';
import { useRouter } from 'vue-router';
import { App } from '@capacitor/app';
import {
  IonCard, IonCardHeader, IonCardTitle, IonCardContent, IonCardSubtitle,
  IonSpinner, IonButton, IonIcon, IonList, IonItem, IonLabel, IonBadge,
  IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonContent,
  IonInput, IonListHeader, IonToggle, IonText, IonNote
} from '@ionic/vue';
import { 
  personAddOutline, createOutline, trashOutline, keyOutline
} from 'ionicons/icons';
import UserList from '@/components/UserList.vue';

// Determine if we're in development mode
const isDevelopment = import.meta.env.MODE === 'development';
// Check if service role key is available
const hasServiceRoleKey = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY;

// User store
const userStore = useUserStore();
const { showToast } = useToast();
const router = useRouter();

// Computed properties
const isSuperAdmin = computed(() => {
  const user = userStore.user.value;
  return user?.isAdmin && user?.isSuperAdmin;
});

// User interface
interface User {
  id: string;
  email?: string;
  username?: string;
  is_admin?: boolean;
  is_super_admin?: boolean;
  is_owner?: boolean;
  rooftop_id?: string;
}

// State
const loading = ref(true);
const users = ref<User[]>([]);
const showAddUserModal = ref(false);
const showEditUserModal = ref(false);
const showDeleteConfirmModal = ref(false);
const creatingUser = ref(false);
const updatingUser = ref(false);
const deletingUser = ref(false);
const corsError = ref(false);
const resettingPassword = ref(false);
// Flag to track if initial load is completed
const initialLoadCompleted = ref(false);

// Email cache to avoid repeated API calls
const emailCache = ref<Record<string, string>>({});

// Form state
const newUser = ref({
  email: '',
  username: '',
  password: '',
  is_admin: false,
  is_super_admin: false,
  is_owner: false
});

const editingUser = ref<User | null>(null);
const userToDelete = ref<User | null>(null);

// State for password reset help modal
const showPasswordHelpModal = ref(false);
const selectedUser = ref<User | null>(null);

// Load users when the component mounts
onMounted(async () => {
  console.log('UserManagementAltView mounted');
  
  try {
    // Get current session
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    
    // Ensure user store is initialized
    if (currentSession?.user) {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', currentSession.user.id)
        .single();

      if (!profileError && profile) {
        await userStore.setUser({
          id: profile.id,
          username: profile.username,
          isAdmin: profile.is_admin,
          isSuperAdmin: profile.is_super_admin || false,
          isOwner: profile.is_owner || false,
          rooftop_id: profile.rooftop_id,
          full_name: '',
          avatar_url: '',
          updated_at: ''
        });
      }
    }

    // Set up app state change listener
    App.addListener('appStateChange', async ({ isActive }: { isActive: boolean }) => {
      if (isActive) {
        // Reinitialize user data when app becomes active
        try {
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          if (currentSession?.user) {
            const { data: profile, error: profileError } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', currentSession.user.id)
              .single();

            if (!profileError && profile) {
              await userStore.setUser({
                id: profile.id,
                username: profile.username,
                isAdmin: profile.is_admin,
                isSuperAdmin: profile.is_super_admin || false,
                isOwner: profile.is_owner || false,
                rooftop_id: profile.rooftop_id,
                full_name: '',
                avatar_url: '',
                updated_at: ''
              });
            }
          }
          // Refresh users list after reinitializing user
          await fetchUsers();
        } catch (err) {
          console.error('Error reinitializing user data:', err);
        }
      }
    });

    // Fetch initial users list
    await fetchUsers();
    initialLoadCompleted.value = true;
    
    // Add window focus event listener to force a complete page refresh when user returns to tab
    window.addEventListener('focus', handleWindowFocus);
  } catch (err) {
    console.error('Error in mounted:', err);
    showToast({
      message: 'Error initializing user management',
      color: 'danger'
    });
  }
});

// Clean up when component is destroyed
onBeforeUnmount(() => {
  console.log('UserManagementAltView unmounting');
  // Remove window focus event listener
  window.removeEventListener('focus', handleWindowFocus);
});

// Handle window focus event by doing a full page refresh
const handleWindowFocus = () => {
  console.log('Window focus detected - auto-refresh is disabled');
  // Auto-refresh is disabled for this component
  // The router meta has disableAutoRefresh: true
};

// Fetch users from the database
const fetchUsers = async () => {
  try {
    loading.value = true;
    console.log('Fetching users...');
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get the rooftop_id from the user's profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('rooftop_id')
      .eq('id', user.id)
      .single();

    if (profileError) throw profileError;
    if (!profile.rooftop_id) throw new Error('No rooftop ID found');

    // Fetch users with this rooftop_id, including email
    const { data, error } = await supabase
      .from('profiles')
      .select('id, username, email, is_admin, is_super_admin, is_owner, rooftop_id')
      .eq('rooftop_id', profile.rooftop_id);

    if (error) throw error;

    users.value = data;
    console.log('User list loaded with emails from profiles table');
  } catch (error: any) {
    console.error('Error fetching users:', error);
    showToast({
      message: `Error loading users: ${error.message}`,
      color: 'danger'
    });
  } finally {
    loading.value = false;
  }
};

// Create a new user
const createUser = async () => {
  try {
    // Set creating state
    creatingUser.value = true;
    console.log('Creating new user:', { ...newUser.value, password: '***REDACTED***' });
    
    // Validate inputs
    if (!newUser.value.email || !newUser.value.username || !newUser.value.password) {
      throw new Error('Please fill in all required fields');
    }
    
    if (newUser.value.password.length < 6) {
      throw new Error('Password must be at least 6 characters');
    }
    
    // Get the current user's rooftop_id
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    
    const { data: adminProfile, error: adminProfileError } = await supabase
      .from('profiles')
      .select('rooftop_id')
      .eq('id', user.id)
      .single();
    
    if (adminProfileError) throw adminProfileError;
    if (!adminProfile.rooftop_id) throw new Error('No rooftop ID found');
    
    let newUserId = null;
    
    // Create user with admin API in development mode
    if (isDevelopment && hasServiceRoleKey) {
      console.log('Using admin API to create user');
      
      // Create the auth user
      const { data, error } = await supabase.auth.admin.createUser({
        email: newUser.value.email,
        password: newUser.value.password,
        email_confirm: true
      });
      
      if (error) {
        console.error('Auth creation error:', error);
        
        // Check if user already exists
        if (error.message && error.message.includes('already been registered')) {
          await showToast({
            message: 'A user with this email already exists.',
            color: 'warning'
          });
          return;
        }
        
        throw error;
      }
      
      if (!data || !data.user) {
        throw new Error('Failed to create user - no user data returned');
      }
      
      newUserId = data.user.id;
      console.log('User created successfully in Auth:', newUserId);
    } else {
      // Use edge function in production
      console.log('Using edge function to create user');
      
      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          email: newUser.value.email,
          password: newUser.value.password,
          username: newUser.value.username,
          is_admin: newUser.value.is_admin,
          is_super_admin: newUser.value.is_super_admin,
          rooftop_id: adminProfile.rooftop_id,
          managed_by: user.id
        }
      });
      
      if (error) {
        console.error('Edge function error:', error);
        throw error;
      }
      
      if (!data || !data.user) {
        throw new Error('Failed to create user - no user data returned from edge function');
      }
      
      newUserId = data.user.id;
      console.log('User created successfully via edge function:', newUserId);
      
      // If using edge function, it should handle profile creation too
      // Show success message and return
      await showToast({
        message: 'User created successfully',
        color: 'success'
      });
      
      // Add the new user to the local array
      const newUserObj = {
        id: newUserId,
        username: newUser.value.username,
        email: newUser.value.email,
        is_admin: newUser.value.is_admin,
        is_super_admin: newUser.value.is_super_admin,
        is_owner: false,
        rooftop_id: adminProfile.rooftop_id
      };
      
      // Add to email cache
      emailCache.value[newUserId] = newUser.value.email;
      
      // Add to users array
      users.value = [...users.value, newUserObj];
      
      // Reset form and close modal
      resetNewUserForm();
      showAddUserModal.value = false;
      
      return;
    }
    
    // If we're here, we're in development mode and need to create the profile
    // First check if profile already exists (to avoid duplicate key error)
    const { data: existingProfile } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', newUserId)
      .single();
    
    if (existingProfile) {
      console.log('Profile already exists, updating instead');
      
      // Update existing profile
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          username: newUser.value.username,
          full_name: newUser.value.username,
          is_admin: newUser.value.is_admin,
          is_super_admin: newUser.value.is_super_admin,
          is_owner: false,
          rooftop_id: adminProfile.rooftop_id,
          is_archived: false
        })
        .eq('id', newUserId);
      
      if (updateError) {
        console.error('Profile update error:', updateError);
        throw updateError;
      }
      
      console.log('Profile updated successfully');
    } else {
      console.log('Creating new profile');
      
      // Create new profile
      const { error: insertError } = await supabase
        .from('profiles')
        .insert({
          id: newUserId,
          username: newUser.value.username,
          full_name: newUser.value.username,
          is_admin: newUser.value.is_admin,
          is_super_admin: newUser.value.is_super_admin,
          is_owner: false,
          rooftop_id: adminProfile.rooftop_id
        });
      
      if (insertError) {
        console.error('Profile creation error:', insertError);
        throw insertError;
      }
      
      console.log('Profile created successfully');
    }
    
    // Show success message
    await showToast({
      message: 'User created successfully',
      color: 'success'
    });
    
    // Add the new user to the local array
    const newUserObj = {
      id: newUserId,
      username: newUser.value.username,
      email: newUser.value.email,
      is_admin: newUser.value.is_admin,
      is_super_admin: newUser.value.is_super_admin,
      is_owner: false,
      rooftop_id: adminProfile.rooftop_id
    };
    
    // Add to email cache
    emailCache.value[newUserId] = newUser.value.email;
    
    // Add to users array
    users.value = [...users.value, newUserObj];
    
    // Reset form and close modal
    resetNewUserForm();
    showAddUserModal.value = false;
  } catch (error: any) {
    console.error('Error creating user:', error);
    
    // Handle specific error cases
    if (error.message && error.message.includes('Failed to send a request')) {
      corsError.value = true;
      await showToast({
        message: 'CORS Error: Cannot access Supabase Edge Functions.',
        color: 'danger',
        duration: 6000
      });
    } else {
      await showToast({
        message: `Error creating user: ${error.message}`,
        color: 'danger'
      });
    }
  } finally {
    // Always reset the creating state
    creatingUser.value = false;
  }
};

// Helper to reset the new user form
const resetNewUserForm = () => {
  newUser.value = {
    email: '',
    username: '',
    password: '',
    is_admin: false,
    is_super_admin: false,
    is_owner: false
  };
};

// Open the edit user modal
const openEditUser = async (user: User) => {
  try {
    console.log('Opening edit user modal for:', user);
    // Make a copy of the user object to avoid modifying the original
    const userCopy = { ...user };
    // Use the email from the user object (from profiles)
    editingUser.value = userCopy;
    showEditUserModal.value = true;
  } catch (error) {
    console.error('Error opening edit user modal:', error);
    showToast({
      message: `Error opening edit user modal: ${error}`,
      color: 'danger'
    });
  }
};

// Update a user
const updateUser = async () => {
  if (!editingUser.value) return;
  
  try {
    updatingUser.value = true;
    console.log('Updating user:', editingUser.value);
    
    // Safety check - ensure owner role is always false
    editingUser.value.is_owner = false;
    
    // First, get the current profile to preserve any fields we're not updating
    let currentPassword = null;
    try {
      const { data: currentProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('password')
        .eq('id', editingUser.value.id)
        .single();
      
      if (fetchError) {
        console.warn('Could not fetch current profile password:', fetchError);
      } else if (currentProfile) {
        currentPassword = currentProfile.password;
        console.log('Retrieved existing password from profile');
      }
    } catch (fetchError) {
      console.warn('Error fetching current profile:', fetchError);
      // Continue anyway, we'll just update without preserving the password
    }
    
    console.log('Updating profile in database...');
    
    // Prepare update data
    const updateData: Record<string, any> = {
      username: editingUser.value.username,
      full_name: editingUser.value.username,
      is_admin: editingUser.value.is_admin,
      is_super_admin: editingUser.value.is_super_admin,
      is_owner: false // Always ensure this is false for safety
    };
    
    // Add password if we have it
    if (currentPassword) {
      updateData.password = currentPassword;
    }
    
    // Update the user's profile
    const { error: updateError } = await supabase
      .from('profiles')
      .update(updateData)
      .eq('id', editingUser.value.id);
    
    if (updateError) {
      console.error('Supabase update error:', updateError);
      throw updateError;
    }
    
    console.log('User profile updated successfully in database');
    
    // Update the user in the local array for immediate UI update
    const userIndex = users.value.findIndex(u => u.id === editingUser.value?.id);
    if (userIndex !== -1 && editingUser.value) {
      console.log('Updating user in local array at index:', userIndex);
      users.value[userIndex] = {
        ...users.value[userIndex],
        username: editingUser.value.username,
        is_admin: editingUser.value.is_admin,
        is_super_admin: editingUser.value.is_super_admin,
        is_owner: false
      };
    } else {
      console.warn('Could not find user in local array with ID:', editingUser.value?.id);
    }
    
    // Show success message
    await showToast({
      message: 'User updated successfully',
      color: 'success'
    });
    
    // Close modal
    showEditUserModal.value = false;
  } catch (error: any) {
    console.error('Error updating user:', error);
    await showToast({
      message: `Error updating user: ${error.message}`,
      color: 'danger'
    });
  } finally {
    // Always reset the updating state
    updatingUser.value = false;
    // Clear the editing user
    editingUser.value = null;
  }
};

// Confirm delete user
const confirmDeleteUser = (user: User) => {
  userToDelete.value = user;
  showDeleteConfirmModal.value = true;
};

// Delete a user (now archives instead of deleting)
const deleteUser = async () => {
  if (!userToDelete.value) return;
  
  try {
    deletingUser.value = true;
    console.log('Archiving user:', userToDelete.value);
    
    // Update the user's profile to archive them and remove rooftop access
    const { error: profileError } = await supabase
      .from('profiles')
      .update({
        is_archived: true,
        rooftop_id: null, // Remove rooftop access
        is_admin: false,  // Remove admin privileges
        is_super_admin: false, // Remove super admin privileges
        is_owner: false   // Remove owner privileges
      })
      .eq('id', userToDelete.value.id);
    
    if (profileError) {
      console.error('Error archiving user profile:', profileError);
      throw profileError;
    }
    
    console.log('User profile archived successfully');
    
    // If in development mode with service role key, also disable the auth account
    if (isDevelopment && hasServiceRoleKey) {
      try {
        console.log('Disabling auth account in development mode');
        
        // Generate a random secure password they won't know
        const secureRandomPassword = Math.random().toString(36).slice(2) + 
                                    Math.random().toString(36).toUpperCase().slice(2) + 
                                    Math.random().toString(36).slice(2) + 
                                    Date.now().toString(36);
        
        // First, change their password to something they don't know
        await supabase.auth.admin.updateUserById(
          userToDelete.value.id,
          { password: secureRandomPassword }
        );
        
        console.log('User password changed to random value');
        
        // Then disable the account with a ban
        await supabase.auth.admin.updateUserById(
          userToDelete.value.id,
          { 
            ban_duration: '87600h', // Ban for 10 years (effectively permanent)
            email_confirm: false,   // Unconfirm their email
            user_metadata: {        // Add metadata indicating account is disabled
              account_disabled: true,
              disabled_at: new Date().toISOString(),
              disabled_reason: 'User access revoked by admin'
            }
          }
        );
        
        console.log('User auth account disabled successfully');
      } catch (authError) {
        console.warn('Auth update had issues (continuing anyway):', authError);
        // Don't throw here, we'll consider it a success if the profile was updated
      }
    } else {
      // In production, try to use the edge function
      try {
        console.log('Attempting to disable user via edge function');
        await supabase.functions.invoke('disable-user', {
          body: {
            user_id: userToDelete.value.id,
            permanent: true  // Signal that this should be a permanent disabling
          }
        });
        console.log('Edge function called successfully');
      } catch (edgeError) {
        console.warn('Edge function had issues (continuing anyway):', edgeError);
        // Don't throw here, we'll consider it a success if the profile was updated
      }
    }
    
    // Show success message - we consider it a success if the profile was updated
    await showToast({
      message: 'User access permanently revoked',
      color: 'success'
    });
    
    // Remove the user from the local array
    if (userToDelete.value) {
      users.value = users.value.filter(u => u.id !== userToDelete.value?.id);
    }
    
    // Close modal
    showDeleteConfirmModal.value = false;
  } catch (error: any) {
    console.error('Error revoking user access:', error);
    
    if (error.message && error.message.includes('Failed to send a request')) {
      corsError.value = true;
      await showToast({
        message: 'CORS Error: Cannot access Supabase Edge Functions.',
        color: 'danger',
        duration: 6000
      });
    } else {
      await showToast({
        message: `Error revoking user access: ${error.message}`,
        color: 'danger'
      });
    }
  } finally {
    // Always reset the deleting state
    deletingUser.value = false;
    // Clear the user to delete
    userToDelete.value = null;
  }
};

// Show password reset help
const showPasswordResetHelp = (user: User) => {
  selectedUser.value = user;
  showPasswordHelpModal.value = true;
};

// Send password reset - implement actual password reset functionality
const sendPasswordResetEmail = async (user: User | null) => {
  if (!user) return;
  
  try {
    resettingPassword.value = true;
    
    // Extract email from user object or fetch from auth if needed
    let userEmail = user.email || '';
    
    // If email is just the user ID snippet, we need to get the real email
    if (userEmail.includes('User ID:')) {
      // Use the admin API to get user details if in dev mode
      if (isDevelopment && hasServiceRoleKey) {
        const { data, error } = await supabase.auth.admin.getUserById(user.id);
        
        if (error) throw error;
        
        if (data && data.user) {
          userEmail = data.user.email || '';
        } else {
          throw new Error('Could not find user email');
        }
      } else {
        throw new Error('Cannot get user email in this mode');
      }
    }
    
    if (!userEmail) {
      throw new Error('User email is required for password reset');
    }
    
    // Send password reset email using Supabase
    const { error } = await supabase.auth.resetPasswordForEmail(userEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    
    if (error) throw error;
    
    // Show success message
    await showToast({
      message: `Password reset link sent to user's email`,
      color: 'success',
      duration: 5000
    });
    
    // Close the modal
    showPasswordHelpModal.value = false;
    
    // No need to refresh users after sending password reset
  } catch (error: any) {
    console.error('Password reset failed:', error);
    await showToast({
      message: `Error sending reset link: ${error.message}`,
      color: 'danger',
      duration: 6000
    });
  } finally {
    resettingPassword.value = false;
  }
};

// Add handlers for modal dismiss events
const handlePasswordModalDismiss = () => {
  console.log('Password reset modal dismissed');
  showPasswordHelpModal.value = false;
  selectedUser.value = null;
  // Reset password reset state
  resettingPassword.value = false;
  // Don't auto-fetch users when closing modal
};

const handleAddUserModalDismiss = () => {
  console.log('Add user modal dismissed');
  showAddUserModal.value = false;
  creatingUser.value = false;
  resetNewUserForm();
  // Don't auto-fetch users when closing modal
};

const handleEditUserModalDismiss = () => {
  console.log('Edit user modal dismissed');
  showEditUserModal.value = false;
  // Reset form state
  updatingUser.value = false;
  editingUser.value = null;
  // Don't auto-fetch users when closing modal
};

const handleDeleteModalDismiss = () => {
  console.log('Delete confirmation modal dismissed');
  showDeleteConfirmModal.value = false;
  // Reset form state
  deletingUser.value = false;
  userToDelete.value = null;
  // Don't auto-fetch users when closing modal
};

// Function to ensure toggles are visible when the Edit User modal is presented
const onEditUserModalPresent = () => {
  console.log('Edit User modal presented');
  forceModalLightMode();
};

// Function to ensure toggles are visible when the Add User modal is presented
const onAddUserModalPresent = () => {
  console.log('Add User modal presented');
  forceModalLightMode();
};

// Helper function to force light mode on modal elements using JavaScript
const forceModalLightMode = () => {
  setTimeout(() => {
    // Target modal wrappers
    const modalWrappers = document.querySelectorAll('.modal-wrapper');
    modalWrappers.forEach(wrapper => {
      if (wrapper instanceof HTMLElement) {
        wrapper.style.backgroundColor = '#ffffff';
        wrapper.style.background = '#ffffff';
      }
    });

    // Target ion-page elements inside modals
    const modalPages = document.querySelectorAll('.light-mode-modal ion-page, .light-mode-modal .ion-page');
    modalPages.forEach(page => {
      if (page instanceof HTMLElement) {
        page.style.backgroundColor = '#ffffff';
        page.style.background = '#ffffff';
      }
    });
    
    // Add more background styling to other containers
    const modalContainers = document.querySelectorAll('.modal-content, .modal-wrapper-inner, .modal-shadow');
    modalContainers.forEach(container => {
      if (container instanceof HTMLElement) {
        container.style.backgroundColor = '#ffffff';
        container.style.background = '#ffffff';
      }
    });
    
    // Force background on all divs inside the modal
    const modalDivs = document.querySelectorAll('.light-mode-modal div');
    modalDivs.forEach(div => {
      if (div instanceof HTMLElement) {
        div.style.backgroundColor = '#ffffff';
      }
    });
    
    // Enhance labels
    const labels = document.querySelectorAll('.light-mode-modal ion-label');
    labels.forEach(label => {
      if (label instanceof HTMLElement) {
        label.style.color = '#333333';
        label.style.fontSize = '1rem';
        label.style.fontWeight = '500';
        label.style.visibility = 'visible';
        label.style.opacity = '1';
        
        // Check if it's a stacked label
        if (label.getAttribute('position') === 'stacked') {
          label.style.fontSize = '1.1rem';
          label.style.fontWeight = '600';
          label.style.marginBottom = '8px';
          label.style.display = 'block';
      }
    }
  });
    
    // Target form section titles
    const formTitles = document.querySelectorAll('.light-mode-modal h2, .light-mode-modal h3, .light-mode-modal .user-roles');
    formTitles.forEach(title => {
      if (title instanceof HTMLElement) {
        title.style.color = '#333333';
        title.style.fontWeight = '600';
        title.style.fontSize = '1.3rem';
        title.style.marginBottom = '16px';
        title.style.opacity = '1';
      }
    });
  }, 100);
};
</script>

<style scoped>
.user-management-container.appointments-style {
  padding: 112px 20px 0 20px;
  width: 100%;
  box-sizing: border-box;
  background: #f7f9fa;
  min-height: 100vh;
  overflow-y: auto;
  max-height: 100vh;
}
.user-table-card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  padding: 0 0 16px 0;
  margin-bottom: 32px;
  width: 100%;
  /* Remove max-width and margin to allow full width */
}
.user-table-header {
  display: flex;
  align-items: center;
  position: relative;
  background: #fff;
  border-top-left-radius: 14px;
  border-top-right-radius: 14px;
  padding: 24px 32px 12px 32px;
  border-bottom: none;
  min-height: 48px;
  justify-content: space-between;
}
.user-table-title {
  font-size: 2rem;
  font-weight: 700;
  color: #222;
  letter-spacing: 0.01em;
  flex: 1;
  display: flex;
  align-items: center;
}
.user-table-columns {
  display: flex;
  font-weight: 600;
  font-size: 1.1rem;
  border-bottom: 1px solid #e5e5e5;
  padding: 0 32px 12px 32px;
  background: #fff;
}
.user-col {
  flex: 1;
  display: flex;
  align-items: center;
  padding-top: 45px;
}
.user-col.username { flex: 1.5; }
.user-col.email { flex: 1.4; }
.user-col.roles { flex: 1.2; }
.user-col.actions { flex: 0.3; justify-content: flex-start; }
.add-user-button-header {
  margin-left: 24px;
  height: 38px;
  font-size: 1rem;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  position: static;
  align-self: center;
}

/* Force light mode for modals */
.light-mode-modal {
  --backdrop-opacity: 0.6 !important;
  --ion-text-color: #333333 !important;
  --ion-background-color: #ffffff !important;
  --ion-color-scheme: light !important;
  color-scheme: light !important;
  background-color: #ffffff !important;
}

/* Target ion-modal internal parts */
.light-mode-modal::part(content) {
  --ion-text-color: #333333 !important;
  --ion-background-color: #ffffff !important;
  background-color: #ffffff !important;
  color-scheme: light !important;
}

.light-mode-modal::part(backdrop) {
  --backdrop-opacity: 0.6 !important;
  --ion-color-scheme: light !important;
}

/* Target ion-page inside the modal - this is critical */
.light-mode-modal ion-page,
.light-mode-modal .ion-page,
.light-mode-modal ion-content::part(scroll),
.light-mode-modal ion-content .scroll-y,
.light-mode-modal .modal-wrapper,
.light-mode-modal .modal-shadow {
  --background: #ffffff !important;
  background: #ffffff !important;
  --ion-background-color: #ffffff !important;
  color-scheme: light !important;
}

.light-mode-modal ion-header ion-toolbar {
  --background: #ffffff !important;
  --color: #333333 !important;
  --border-color: #e0e0e0 !important;
  color-scheme: light !important;
}

.light-mode-modal ion-title {
  color: #333333 !important;
}

.light-mode-modal ion-buttons ion-button {
  --color: #333333 !important;
}

.light-mode-modal ion-item {
  --background: #ffffff !important;
  --color: #333333 !important;
  --border-color: #e0e0e0 !important;
}

.light-mode-modal ion-label {
  --color: #333333 !important;
  color: #333333 !important;
  font-weight: 500 !important;
  margin-bottom: 8px !important;
  opacity: 1 !important;
  visibility: visible !important;
  font-size: 1rem !important;
}

.light-mode-modal ion-label[position="stacked"] {
  --color: #333333 !important;
  color: #333333 !important; 
  font-weight: 600 !important;
  margin-bottom: 8px !important;
  font-size: 1.1rem !important;
  opacity: 1 !important;
  visibility: visible !important;
  display: block !important;
  transform: none !important;
}

.light-mode-modal ion-input {
  --color: #333333 !important;
  --background: #f5f5f5 !important;
}

.light-mode-modal ion-note {
  color: #666666 !important;
}

.light-mode-modal h2, 
.light-mode-modal h3, 
.light-mode-modal h4,
.light-mode-modal p,
.light-mode-modal strong {
  color: #333333 !important;
}

.light-mode-modal ion-toggle {
  --background: #d7d7d7 !important;
  --background-checked: #838383 !important;
  --handle-background: #ffffff !important;
  --handle-background-checked: #ffffff !important;
  opacity: 1 !important;
  visibility: visible !important;
}

/* Shadow DOM parts for toggles */
.light-mode-modal ion-toggle::part(track) {
  background: var(--background, #d7d7d7) !important;
  opacity: 1 !important;
  visibility: visible !important;
}

.light-mode-modal ion-toggle[aria-checked="true"]::part(track) {
  background: var(--background-checked, #838383) !important; 
}

.light-mode-modal ion-toggle::part(handle) {
  background: var(--handle-background, #ffffff) !important;
  opacity: 1 !important;
  visibility: visible !important;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2) !important;
}

.light-mode-modal ion-toggle[aria-checked="true"]::part(handle) {
  background: var(--handle-background-checked, #ffffff) !important;
}

/* Directly target ion-content and restore its styles */
.light-mode-modal ion-content {
  --background: #ffffff !important;
  background: #ffffff !important;
  --ion-background-color: #ffffff !important;
  color-scheme: light !important;
}

/* Target the global iOS and Material Design wrappers */
.light-mode-modal .modal-wrapper,
.light-mode-modal .modal-wrapper-md,
.light-mode-modal .modal-wrapper-ios {
  background: #ffffff !important;
  --background: #ffffff !important;
  --ion-background-color: #ffffff !important;
  color-scheme: light !important;
}

/* Extremely aggressive styling targeting modal internals by tag */
.light-mode-modal div, .light-mode-modal span, .light-mode-modal form {
  background-color: #ffffff !important;
  color: #333333 !important;
}

/* Target form and inputs specifically */
.light-mode-modal form.compact-form {
  background-color: #ffffff !important;
  color: #333333 !important;
}

/* Target role toggles container */
.light-mode-modal .role-toggles-container {
  background-color: #ffffff !important;
}

/* Apply !important to all child elements to override any specificity issues */
.light-mode-modal * {
  --ion-background-color: #ffffff !important;
  --ion-color-step-50: #f2f2f2 !important;
  --ion-color-step-100: #e6e6e6 !important;
  --ion-color-step-150: #d9d9d9 !important;
  --ion-color-step-200: #cccccc !important;
  --ion-color-step-250: #bfbfbf !important;
  --ion-color-step-300: #b3b3b3 !important;
  --ion-color-step-350: #a6a6a6 !important;
  --ion-color-step-400: #999999 !important;
  --ion-color-step-450: #8c8c8c !important;
  --ion-color-step-500: #808080 !important;
  --ion-color-step-550: #737373 !important;
  --ion-color-step-600: #666666 !important;
  --ion-color-step-650: #595959 !important;
  --ion-color-step-700: #4d4d4d !important;
  --ion-color-step-750: #404040 !important;
  --ion-color-step-800: #333333 !important;
  --ion-color-step-850: #262626 !important;
  --ion-color-step-900: #191919 !important;
  --ion-color-step-950: #0d0d0d !important;
  --ion-text-color: #333333 !important;
  color-scheme: light !important;
}

/* Additional styles to target deep shadow DOM and internal structure */
:host {
  --ion-background-color: #ffffff !important;
  --background: #ffffff !important;
  background: #ffffff !important;
  color-scheme: light !important;
}

/* Target parent wrapper */
.light-mode-modal sc-ion-modal-md,
.light-mode-modal sc-ion-modal-ios {
  --background: #ffffff !important;
  background: #ffffff !important;
  --ion-background-color: #ffffff !important;
}

/* Target presentation styles */
.modal-wrapper.sc-ion-modal-md,
.modal-wrapper.sc-ion-modal-ios {
  --background: #ffffff !important;
  background: #ffffff !important;
  --ion-background-color: #ffffff !important;
}

/* Target Android-specific components */
.md .modal-wrapper ion-backdrop {
  background-color: rgba(0,0,0,0.6) !important;
}

.md .modal-wrapper .modal-wrapper {
  background: #ffffff !important;
  --background: #ffffff !important;
}

/* Target iOS-specific components */
.ios .modal-wrapper ion-backdrop {
  background-color: rgba(0,0,0,0.6) !important;
}

.ios .modal-wrapper .modal-wrapper {
  background: #ffffff !important;
  --background: #ffffff !important;
}

/* Force form backgrounds */
.light-mode-modal form,
.light-mode-modal .compact-form {
  background-color: #ffffff !important;
}

/* Add more space between label and input */
.light-mode-modal ion-item.compact-item {
  --inner-padding-top: 8px !important;
  --inner-padding-bottom: 8px !important;
  margin-bottom: 16px !important;
}

/* Ensure form section headers are visible */
.light-mode-modal ion-list-header,
.light-mode-modal .compact-header {
  --color: #333333 !important;
  color: #333333 !important;
  font-weight: 600 !important;
  font-size: 1.2rem !important;
  opacity: 1 !important;
}

/* Style form titles */
.light-mode-modal h2, 
.light-mode-modal h3,
.light-mode-modal .user-roles {
  color: #333333 !important;
  font-weight: 600 !important;
  font-size: 1.3rem !important;
  margin-bottom: 16px !important;
  opacity: 1 !important;
}

.edit-user-force-black,
.edit-user-force-black * {
  --ion-text-color: #000000 !important;
  --color: #000000 !important;
  color: #000000 !important;
  --ion-color-base: #000000 !important;
  --ion-color-step-800: #000000 !important;
  --ion-color-step-900: #000000 !important;
  --ion-color-step-950: #000000 !important;
}

.edit-user-force-black ion-label,
.edit-user-force-black ion-title,
.edit-user-force-black ion-note,
.edit-user-force-black ion-list-header,
.edit-user-force-black h2,
.edit-user-force-black h3,
.edit-user-force-black h4,
.edit-user-force-black p,
.edit-user-force-black strong {
  color: #000000 !important;
}

.edit-user-force-black ion-input,
.edit-user-force-black input,
.edit-user-force-black textarea {
  --color: #000000 !important;
  color: #000000 !important;
  caret-color: #000000 !important;
}

.edit-user-force-black ion-toggle {
  --color: #000000 !important;
}
</style>
